//////////////////
// IMPORTS
//////////////////
// schema
import * as ISchema from "@schema";
// libraries
import * as React from "react";
import { QueryRenderer, ReadyState } from "react-relay";
// event imports
import { IRendererProps } from "./Event.d";
import { Event } from "./Event";
import { EventMismatch } from "./EventMismatch";
import { UncrawledEvent } from "./UncrawledEvent";
import { eventRendererQuery } from "./EventRenderer.gql";
// other internals
import { CalloutBox } from "@components/callout-box";
import { PainOrPriorityEvent } from "@components/pain-or-priority-event";
import { CircularProgress } from "@clockwise/web-commons/src/components/circular-progress";
import * as s from "./Event.styles";
// util
import { logger } from "@util/logger.util";
import { isLinkedSmartHold } from "@util/event.util";
import { eventFromParent } from "@clockwise/client-commons/src/util/event";
import {
  getDomainFromCalendarId,
  isShadowCalendarId,
  WEIRD_VALID_GOOGLE_RESOURCE_CALENDAR_DOMAINS,
} from "@util/calendar-id.util";
import { handleReadyStateError } from "@util/relay.util";
import { EventMissing } from "./EventMissing";
// material-ui

//////////////////
// RENDERER
//////////////////
export class EventRenderer extends React.Component<IRendererProps> {
  // used to render the props from the QueryRenderer
  private renderCallback = (viewer: ISchema.IViewer, org: ISchema.IOrg, calendarId: string) => {
    const { externalEventId } = this.props;

    if (!viewer.user || !org) {
      // note: slightly weird to show this if no org or working gorup
      // but probably fine...
      logger.warn("missing some data to render this event");
      return null;
    }

    let isShadowCalendar = false;
    if (
      org.shadowCalendarSettingsErrorable &&
      org.shadowCalendarSettingsErrorable.__typename === "ShadowCalendarSettings"
    ) {
      const shadowCalendarId = org.shadowCalendarSettingsErrorable.shadowCalendarId;
      isShadowCalendar = isShadowCalendarId(calendarId, shadowCalendarId);
    }
    const calendarDomain = getDomainFromCalendarId(calendarId);
    const isMismatch =
      [...org.domains, ...WEIRD_VALID_GOOGLE_RESOURCE_CALENDAR_DOMAINS].indexOf(calendarDomain) ===
        -1 && !isShadowCalendar;
    const isUncrawledEvent =
      [
        ...org.crawledCalendarInfoForCalendars.synced,
        ...org.crawledCalendarInfoForCalendars.pending,
      ].indexOf(calendarId) < 0;

    if (isMismatch) {
      return <EventMismatch />;
    }

    if (isUncrawledEvent && !isShadowCalendar && !externalEventId.startsWith("teamevent")) {
      return (
        <div style={{ paddingLeft: 8 }}>
          <UncrawledEvent />
        </div>
      );
    }

    if (org.forceFetchEventParentErrorable.__typename === "GraphEntityError") {
      return org.forceFetchEventParentErrorable.statusCode === 404 ? (
        <EventMissing />
      ) : (
        <CalloutBox error>
          An error occurred while trying to load this event. Please try viewing this event again
          later.
        </CalloutBox>
      ); // error
    }

    const eventParent = org.forceFetchEventParentErrorable;
    const event = eventFromParent(eventParent, calendarId);
    const isPainOrPriorityEvent =
      event &&
      event.annotatedEvent &&
      event.annotatedEvent.orgTags &&
      event.annotatedEvent.orgTags.find((ot) => ot.state && ot.state.value === "Pain");

    if (isPainOrPriorityEvent) {
      return <PainOrPriorityEvent org={org} />;
    }

    let canRescheduleEvent = false;
    let canAutopilotEvent = false;
    let attendeeNotInDomain = false;
    let organizerNotClockwiseUser = false;
    let isAllDayEvent = false;

    if (
      org.canScheduleResponse.__typename === "CanScheduleResponseList" &&
      org.canScheduleResponse.list.length
    ) {
      const canScheduleResponseEntry = org.canScheduleResponse.list[0];

      canRescheduleEvent = canScheduleResponseEntry.canReschedule;
      canAutopilotEvent = canScheduleResponseEntry.canAutopilot;
      attendeeNotInDomain = canScheduleResponseEntry.attendeeNotInDomain;
      organizerNotClockwiseUser = canScheduleResponseEntry.organizerNotClockwiseUser;
      isAllDayEvent = canScheduleResponseEntry.isAllDayEvent;
    }

    let smartHoldEventInfo: ISchema.ISmartHoldEventInfo | null = null;
    if (org.smartHoldEventInfo && org.smartHoldEventInfo.__typename === "SmartHoldEventInfo") {
      smartHoldEventInfo = org.smartHoldEventInfo;
    }

    return (
      <Event
        org={org}
        user={viewer.user}
        eventParent={eventParent}
        smartHoldEventInfo={smartHoldEventInfo}
        calendarId={calendarId}
        canReschedule={canRescheduleEvent}
        canAutopilot={canAutopilotEvent}
        attendeeNotInDomain={attendeeNotInDomain}
        organizerNotClockwiseUser={organizerNotClockwiseUser}
        isAllDayEvent={isAllDayEvent}
        withBorder={this.props.withBorder}
      />
    );
  };

  public render() {
    const { org, calendarId, externalEventId, environment } = this.props;
    if (!calendarId) {
      logger.error(`missing calendarId for event, cannot render`, { externalEventId });
      return <div />;
    }

    const orgRelayId = org.id;

    return (
      <QueryRenderer
        environment={environment}
        query={eventRendererQuery}
        variables={{
          orgRelayId,
          calendarId,
          externalEventId,
          externalEventIds: [externalEventId],
          calendarIds: [calendarId],
          isLinkedSmartHold: isLinkedSmartHold(externalEventId),
        }}
        cacheConfig={{ force: true }}
        render={(readyState: ReadyState) => {
          const freshViewer = readyState.props && readyState.props.viewer;
          const freshOrg = readyState.props && readyState.props.org;

          // check for error or show a loader for first load
          if (readyState.error) {
            handleReadyStateError(readyState.error, "EventRenderer");

            return (
              <CalloutBox error>An error has occurred. Please try refreshing the page.</CalloutBox>
            );
          }

          if (freshViewer && freshOrg) {
            return this.renderCallback(freshViewer, freshOrg, calendarId);
          } else {
            return (
              <div style={s.loadingContainer}>
                <CircularProgress />
              </div>
            );
          }
        }}
      />
    );
  }
}
